import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const Login = () => {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState('');
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Redireciona se já estiver logado
  useEffect(() => {
    if (!loading && user) {
      // Pequeno delay para estabilizar o DOM antes do redirecionamento
      const timer = setTimeout(() => {
        if (isMounted.current) {
          navigate('/home');
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, loading, navigate]);

  async function handleLogin() {
    if (isLoggingIn) return;
    
    setIsLoggingIn(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Delay para garantir estabilidade do DOM
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const result = await signInWithPopup(auth, provider);
      
      // criar/atualizar doc no Firestore imediatamente para garantir aparecimento no painel
      const user = result.user;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            uid: user.uid,
            name: user.displayName || '',
            email: user.email,
            emailVerified: user.emailVerified || false,
            platform: user.providerData[0]?.providerId || 'google',
            createdAt: new Date(),
            lastLogin: new Date(),
            plan: 'free',
            active: true
          });
        } else {
          await updateDoc(userRef, { lastLogin: new Date() });
        }
      } catch (fireError) {
        console.error('Erro ao criar/atualizar usuário no Firestore após login:', fireError);
      }

      if (isMounted.current) {
        console.log('Login bem-sucedido:', result.user.email);
        // O redirecionamento será tratado pelo useEffect acima
      }
      
    } catch (error) {
      if (isMounted.current) {
        console.error('Erro detalhado no login:', error);
        
        let errorMessage = 'Erro ao tentar login. Tente novamente.';
        
        if (error.code === 'auth/popup-closed-by-user') {
          errorMessage = 'Autenticação cancelada.';
        } else if (error.code === 'auth/popup-blocked') {
          errorMessage = 'Popup bloqueado. Permita popups para este site.';
        } else if (error.code === 'auth/network-request-failed') {
          errorMessage = 'Erro de conexão. Verifique sua internet.';
        }
        
        setError(errorMessage);
        setIsLoggingIn(false);
      }
    }
  }

  // Loading inicial do contexto
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Se já estiver logado, mostra loading até redirecionar
  if (user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-success mb-3" role="status">
            <span className="visually-hidden">Redirecionando...</span>
          </div>
          <p>Redirecionando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container min-vh-100 d-flex align-items-center">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card shadow-lg border-0">
              <div className="card-body text-center p-5">
                <div className="mb-4">
                  <i className="fas fa-lock fa-3x text-primary mb-3"></i>
                  <h2 className="card-title">Acesse sua conta</h2>
                  <p className="text-muted">
                    Faça login para acessar todos os recursos do Lotopim
                  </p>
                </div>

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setError('')}
                    ></button>
                  </div>
                )}

                <button 
                  className="btn btn-primary w-100 py-2 mb-3"
                  onClick={handleLogin}
                  disabled={isLoggingIn}
                  style={{
                    opacity: isLoggingIn ? 0.7 : 1,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isLoggingIn ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      Autenticando...
                    </>
                  ) : (
                    <>
                      <i className="fab fa-google me-2"></i>
                      Entrar com Google
                    </>
                  )}
                </button>

                <div className="mt-4">
                  <small className="text-muted">
                    Ao fazer login, você concorda com nossos Termos de Serviço e Política de Privacidade
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;